import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Pencil, Plus, Search, Share2, Spline, Trash2, Users } from 'lucide-react';
import { STARTER_TAGS, can, diagramIsEmpty, type Diagram, type Drill, type TeamSummary } from '@hoopify/shared';
import { api, errorMessage, fieldErrors } from '../lib/api';
import { Alert, Button, Dialog, Empty, Field, LoadingBlock, PageHeader, TextArea } from '../components/ui';
import { DiagramView } from '../components/diagram/DiagramView';
import { DiagramEditorDialog } from '../components/diagram/DiagramEditor';
import { TagPicker } from '../components/TagPicker';
import { LibraryBrowser } from '../components/library/LibraryBrowser';
import { useSearchParams } from 'react-router';

type Scope = 'mine' | 'shared' | 'hoopify';

export function DrillsPage() {
  const [params, setParams] = useSearchParams();
  const scope: Scope = params.get('tab') === 'hoopify' ? 'hoopify' : params.get('tab') === 'shared' ? 'shared' : 'mine';
  const setScope = (s: Scope) => setParams(s === 'mine' ? {} : { tab: s }, { replace: true });
  const [q, setQ] = useState('');
  const [tag, setTag] = useState<string | null>(null);
  const [editing, setEditing] = useState<Drill | 'new' | null>(null);
  const [viewing, setViewing] = useState<Drill | null>(null);

  const query = new URLSearchParams({ scope: scope === 'hoopify' ? 'mine' : scope, ...(q.trim() ? { q: q.trim() } : {}), ...(tag ? { tag } : {}) });
  const drills = useQuery({ queryKey: ['drills', scope, q.trim(), tag], queryFn: () => api.get<Drill[]>(`/drills?${query}`), placeholderData: (p) => p, enabled: scope !== 'hoopify' });
  const allTags = useQuery({ queryKey: ['drill-tags'], queryFn: () => api.get<string[]>('/drills/tags') });

  return (
    <>
      <PageHeader
        title="Drills"
        description="Your library of drill ideas. Add them to any session plan, then change them there to suit the day."
        actions={
          <Button onClick={() => setEditing('new')}>
            <Plus className="size-4" aria-hidden /> New drill
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="grid grid-cols-3 gap-1 rounded-md bg-sunken p-1 text-[14px]" role="tablist">
          {(['mine', 'shared', 'hoopify'] as const).map((s) => (
            <button
              key={s}
              role="tab"
              aria-selected={scope === s}
              onClick={() => setScope(s)}
              className={`rounded px-3 py-1 font-medium ${scope === s ? 'bg-surface text-ink shadow-sm' : 'text-muted hover:text-ink'}`}
            >
              {s === 'mine' ? 'My drills' : s === 'shared' ? 'Shared with me' : 'Hoopify library'}
            </button>
          ))}
        </div>
        {scope !== 'hoopify' && <div className="relative min-w-[200px] flex-1 sm:max-w-[320px]">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-faint" aria-hidden />
          <input className="field-input pl-8" placeholder="Search name, description or coaching points" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search drills" />
        </div>}
      </div>
      {scope === 'hoopify' ? <LibraryBrowser /> : <>
      <div className="mb-6 flex flex-wrap gap-1.5" aria-label="Filter by tag">
        {(allTags.data ?? STARTER_TAGS).map((t) => (
          <button
            key={t}
            onClick={() => setTag(tag === t ? null : t)}
            aria-pressed={tag === t}
            className={`rounded-full border px-2.5 py-0.5 text-[13px] ${tag === t ? 'border-action bg-action text-action-ink' : 'border-line-strong text-muted hover:text-ink'}`}
          >
            {t}
          </button>
        ))}
      </div>

      {drills.isLoading ? (
        <LoadingBlock />
      ) : drills.isError ? (
        <Alert>{errorMessage(drills.error)}</Alert>
      ) : !drills.data?.length ? (
        q || tag ? (
          <Empty title="No drills match">Try a different word or tag.</Empty>
        ) : scope === 'mine' ? (
          <Empty
            title="Your library is empty"
            action={
              <div className="flex flex-wrap justify-center gap-2">
                <Button onClick={() => setScope('hoopify')}>Browse the Hoopify library</Button>
                <Button variant="secondary" onClick={() => setEditing('new')}>
                  Add your own drill
                </Button>
              </div>
            }
          >
            Start with drills from the Hoopify library, or save your own with coaching points, equipment and a court diagram.
          </Empty>
        ) : (
          <Empty title="Nothing shared with you yet">
            When another coach on one of your teams shares a drill, it shows up here. You can copy it into your own library.
          </Empty>
        )
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface">
          {drills.data.map((d) => (
            <li key={d.id}>
              <button onClick={() => setViewing(d)} className="flex w-full items-start gap-4 px-4 py-3 text-left hover:bg-sunken sm:px-5">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{d.title}</p>
                  {(d.description || d.coachingPoints) && <p className="mt-0.5 line-clamp-1 text-[14px] text-muted">{d.description ?? d.coachingPoints}</p>}
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[12px]">
                    {d.tags.map((t) => (
                      <span key={t} className="rounded bg-sunken px-1.5 py-0.5 text-muted">
                        {t}
                      </span>
                    ))}
                    {d.diagram && !diagramIsEmpty(d.diagram) && (
                      <span className="inline-flex items-center gap-1 text-muted">
                        <Spline className="size-3.5" aria-hidden /> Diagram
                      </span>
                    )}
                    {d.mine && d.sharedWith.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-action">
                        <Users className="size-3.5" aria-hidden /> Shared with {d.sharedWith.map((s) => s.teamName).join(', ')}
                      </span>
                    )}
                    {!d.mine && <span className="text-muted">From {d.owner.name}</span>}
                  </div>
                </div>
                <span className="tnum shrink-0 text-[14px] text-muted">{d.minutes} min</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      </>}
      {viewing && (
        <DrillView
          drill={viewing}
          onClose={() => setViewing(null)}
          onEdit={() => {
            setEditing(viewing);
            setViewing(null);
          }}
        />
      )}
      {editing && <DrillEditor drill={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function DrillView({ drill: d, onClose, onEdit }: { drill: Drill; onClose: () => void; onEdit: () => void }) {
  const qc = useQueryClient();
  const [sharing, setSharing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const copy = useMutation({
    mutationFn: () => api.post<Drill>(`/drills/${d.id}/copy`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drills'] }),
  });
  const remove = useMutation({
    mutationFn: () => api.del(`/drills/${d.id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['drills'] });
      onClose();
    },
  });
  const facts: [string, string | null][] = [
    ['How it runs', d.description],
    ['Coaching points', d.coachingPoints],
    ['Equipment', d.equipment],
    ['Players', d.minPlayers || d.maxPlayers ? [d.minPlayers, d.maxPlayers].filter(Boolean).join(' to ') : null],
    ['Courts', d.courts ? String(d.courts) : null],
  ];

  if (sharing) return <ShareDialog drill={d} onClose={() => setSharing(false)} />;

  return (
    <Dialog open onClose={onClose} title={d.title} size={d.diagram && !diagramIsEmpty(d.diagram) ? 'lg' : 'md'}>
      <div className="max-h-[65dvh] space-y-3 overflow-y-auto">
        {d.diagram && !diagramIsEmpty(d.diagram) && <DiagramView diagram={d.diagram} />}
        <p className="tnum text-[14px] text-muted">
          {d.minutes} min{d.tags.length ? `, ${d.tags.join(', ')}` : ''}
          {!d.mine ? `. Shared by ${d.owner.name}.` : ''}
        </p>
        {facts
          .filter(([, v]) => v)
          .map(([k, v]) => (
            <div key={k}>
              <p className="text-[12px] font-semibold text-muted">{k}</p>
              <p className="whitespace-pre-line">{v}</p>
            </div>
          ))}
        {d.mine && (
          <p className="text-[14px] text-muted">{d.sharedWith.length ? `Shared with coaches on ${d.sharedWith.map((s) => s.teamName).join(', ')}.` : 'Only you can see this drill.'}</p>
        )}
      </div>
      {copy.isSuccess && <div className="mt-4"><Alert tone="ok">Copied to your library. You can change your copy however you like.</Alert></div>}
      {(copy.isError || remove.isError) && <div className="mt-4"><Alert>{errorMessage(copy.error ?? remove.error)}</Alert></div>}
      {confirmDelete ? (
        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          <span className="mr-auto text-[14px]">Delete this drill? Plans that use it keep their copy.</span>
          <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
            Keep it
          </Button>
          <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
            Delete
          </Button>
        </div>
      ) : (
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          {d.mine ? (
            <>
              <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                <Trash2 className="size-4" aria-hidden /> Delete
              </Button>
              <Button variant="secondary" onClick={() => setSharing(true)}>
                <Share2 className="size-4" aria-hidden /> Share
              </Button>
              <Button onClick={onEdit}>
                <Pencil className="size-4" aria-hidden /> Edit
              </Button>
            </>
          ) : (
            !copy.isSuccess && (
              <Button loading={copy.isPending} onClick={() => copy.mutate()}>
                <Copy className="size-4" aria-hidden /> Copy to my library
              </Button>
            )
          )}
        </div>
      )}
    </Dialog>
  );
}

function ShareDialog({ drill, onClose }: { drill: Drill; onClose: () => void }) {
  const qc = useQueryClient();
  const teams = useQuery({ queryKey: ['teams'], queryFn: () => api.get<TeamSummary[]>('/teams') });
  const coachTeams = (teams.data ?? []).filter((t) => can(t.role, 'sessions:write'));
  const [chosen, setChosen] = useState<string[]>(drill.sharedWith.map((s) => s.teamId));
  const save = useMutation({
    mutationFn: () => api.put<Drill>(`/drills/${drill.id}/shares`, { teamIds: chosen }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['drills'] });
      onClose();
    },
  });
  return (
    <Dialog open onClose={onClose} title={`Share ${drill.title}`}>
      <p className="text-muted">Coaches on the teams you tick can see this drill and copy it into their own library. They can’t change yours. Players and parents never see your library.</p>
      {teams.isLoading ? (
        <LoadingBlock />
      ) : !coachTeams.length ? (
        <p className="mt-4">You’re not coaching any teams yet.</p>
      ) : (
        <fieldset className="mt-4 space-y-1">
          <legend className="sr-only">Teams</legend>
          {coachTeams.map((t) => (
            <label key={t.id} className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-sunken">
              <input
                type="checkbox"
                className="size-4 accent-[var(--action)]"
                checked={chosen.includes(t.id)}
                onChange={(e) => setChosen(e.target.checked ? [...chosen, t.id] : chosen.filter((x) => x !== t.id))}
              />
              <span>{t.name}</span>
            </label>
          ))}
        </fieldset>
      )}
      {save.isError && <div className="mt-3"><Alert>{errorMessage(save.error)}</Alert></div>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button loading={save.isPending} onClick={() => save.mutate()} disabled={!coachTeams.length}>
          Save sharing
        </Button>
      </div>
    </Dialog>
  );
}

function DrillEditor({ drill, onClose }: { drill: Drill | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [v, setV] = useState({
    title: drill?.title ?? '',
    minutes: String(drill?.minutes ?? 10),
    description: drill?.description ?? '',
    coachingPoints: drill?.coachingPoints ?? '',
    equipment: drill?.equipment ?? '',
    minPlayers: drill?.minPlayers ? String(drill.minPlayers) : '',
    maxPlayers: drill?.maxPlayers ? String(drill.maxPlayers) : '',
    courts: drill?.courts ? String(drill.courts) : '',
  });
  const [tags, setTags] = useState<string[]>(drill?.tags ?? []);
  const [diagram, setDiagram] = useState<Diagram | null>(drill?.diagram ?? null);
  const [drawing, setDrawing] = useState(false);
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        ...v,
        minPlayers: v.minPlayers || null,
        maxPlayers: v.maxPlayers || null,
        courts: v.courts || null,
        tags,
        diagram,
      };
      return drill ? api.patch<Drill>(`/drills/${drill.id}`, body) : api.post<Drill>('/drills', body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['drills'] });
      void qc.invalidateQueries({ queryKey: ['drill-tags'] });
      onClose();
    },
  });
  const f = fieldErrors(save.error);

  return (
    <Dialog open onClose={onClose} title={drill ? 'Edit drill' : 'New drill'} size="lg">
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }} className="max-h-[70dvh] space-y-4 overflow-y-auto pr-1" noValidate>
        {save.isError && !Object.keys(f).length && <Alert>{errorMessage(save.error)}</Alert>}
        <div className="grid grid-cols-[1fr_96px] gap-3">
          <Field label="Name" value={v.title} onChange={set('title')} error={f.title} maxLength={120} autoFocus placeholder="e.g. Shell drill" />
          <Field label="Minutes" type="number" min={1} max={180} value={v.minutes} onChange={set('minutes')} error={f.minutes} className="field-input tnum" />
        </div>
        <TextArea label="How it runs" value={v.description} onChange={set('description')} error={f.description} maxLength={4000} placeholder="Set-up, rotation, scoring…" />
        <TextArea label="Coaching points" value={v.coachingPoints} onChange={set('coachingPoints')} error={f.coachingPoints} maxLength={4000} placeholder="What to look for and call out" />
        <Field label="Equipment" value={v.equipment} onChange={set('equipment')} error={f.equipment} maxLength={500} placeholder="e.g. 6 cones, a ball each" />
        <div className="grid grid-cols-3 gap-3">
          <Field label="Min. players" type="number" min={1} max={60} value={v.minPlayers} onChange={set('minPlayers')} error={f.minPlayers} className="field-input tnum" />
          <Field label="Max. players" type="number" min={1} max={60} value={v.maxPlayers} onChange={set('maxPlayers')} error={f.maxPlayers} className="field-input tnum" />
          <Field label="Courts" type="number" min={1} max={6} value={v.courts} onChange={set('courts')} error={f.courts} className="field-input tnum" />
        </div>
        <div>
          <p className="mb-1.5 text-[14px] font-medium">Tags</p>
          <TagPicker value={tags} onChange={setTags} />
          {f.tags && <p className="mt-1 text-[13px] text-danger">{f.tags}</p>}
        </div>
        <div>
          <p className="mb-1.5 text-[14px] font-medium">Court diagram</p>
          {diagram && !diagramIsEmpty(diagram) ? (
            <div className="space-y-2">
              <DiagramView diagram={diagram} />
              <Button type="button" variant="secondary" onClick={() => setDrawing(true)}>
                <Spline className="size-4" aria-hidden /> Edit diagram
              </Button>
            </div>
          ) : (
            <Button type="button" variant="secondary" onClick={() => setDrawing(true)}>
              <Spline className="size-4" aria-hidden /> Draw a diagram
            </Button>
          )}
          {f.diagram && <p className="mt-1 text-[13px] text-danger">{f.diagram}</p>}
        </div>
        <DiagramEditorDialog
          open={drawing}
          value={diagram}
          title={v.title ? `Diagram: ${v.title}` : 'Court diagram'}
          onClose={() => setDrawing(false)}
          onSave={(d) => {
            setDiagram(d);
            setDrawing(false);
          }}
        />
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending}>
            {drill ? 'Save changes' : 'Add drill'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
